// Function index:
//   mountHexScene(parent, opts) — boot a Pixi-rendered pointy-top hex scene
//       with pan/zoom, multi-character tokens, click-to-move adjacency, and
//       Phase B3 viewport culling. Returns a SceneMount handle the caller
//       drives via WS events.
//
// Layers (back to front, flat): tileLayer, roadLayer, overlayLayer,
// locationLayer, tokenLayer. tileLayer + tokenLayer are owned by the
// Renderer (hex-scene/renderer.ts); this file owns roadLayer/overlayLayer/
// locationLayer directly and talks to tiles/tokens only through the
// Renderer interface. Two implementers: default FlatRenderer
// (hex-scene/flat-renderer.ts), wrapping the same tile culling
// (hex-scene/culling.ts) and token lifecycle (hex-scene/token-registry.ts) as
// before the seam; and IsoRenderer (hex-scene/iso/iso-renderer.ts), selected
// when the map style is 'iso' (atlas.ts's mapStyle — opt-in from the options
// gear) AND every terrain on screen has a loaded texture
// (atlas.ts's terrainTilesLoaded). Under iso, IsoRenderer's single worldLayer (tiles+tokens,
// depth-sorted) inserts itself BEHIND roadLayer/overlayLayer/locationLayer
// (back to front: worldLayer, roadLayer, overlayLayer, locationLayer) so
// those overlays render above the tile art instead of being hidden by it;
// their positions also switch to iso/project.ts's isoProject/isoHexCorners
// (see `cornersFor`/`toPixel`/the doorway-marker projection below) instead
// of the flat pointy-top HexTile pixels. Static helpers live in siblings:
// hex-scene/locations.ts (doorway marker + kind tables),
// hex-scene/streaming.ts (distance + adjacency + stream trigger),
// hex-scene/fog.ts (view-ring + visibility pure functions).

import { Application, Container, Graphics } from 'pixi.js';
import { HexTile, TERRAIN_TEXTURE } from './hex-scene/palette.js';
import { installCameraControls } from './hex-scene/camera.js';
import { tweenAlpha } from './hex-scene/anim.js';
import { getTerrainTexture, loadHexAtlas, mapStyle, terrainTilesLoaded } from './hex-scene/atlas.js';
import { renderOverlays } from './hex-scene/overlays.js';
import { FlatRenderer } from './hex-scene/flat-renderer.js';
import { IsoRenderer } from './hex-scene/iso/iso-renderer.js';
import { FACE_W, isoHexCorners, isoProject } from './hex-scene/iso/project.js';
import type { Renderer, RendererCtx } from './hex-scene/renderer.js';
import type { OverlayCtx } from './hex-scene/overlays.js';
import type {
  CharacterInput, HexInput, SceneMount, SceneOptions,
} from './hex-scene/types.js';
import { addDoorwayMarker } from './hex-scene/locations.js';
import { createStreamTrigger, isAdjacent } from './hex-scene/streaming.js';
import { drawRoads, roadSegments } from './hex-scene/roads.js';
import { computeViewKeys, touchedFogKeys, visibilityFor } from './hex-scene/fog.js';

export type { HexInput, CharacterInput, SceneMount, SceneOptions };

export async function mountHexScene(parent: HTMLElement, opts: SceneOptions): Promise<SceneMount> {
  const app = new Application();
  await app.init({ resizeTo: parent, background: 0x0a0a0a, antialias: true });
  parent.appendChild(app.canvas);

  // Fetch art for THIS world's terrains only — an interior (floor/wall/door/
  // feature, none of which have tiles) fetches nothing at all. Renderers read
  // the loaded frames through atlas.ts's getTerrainTexture, which self-guards
  // to null, so the boolean result isn't needed here.
  const terrains = new Set(opts.hexes.map((h) => h.terrain));
  await loadHexAtlas(terrains);

  // Renderer selection gate. The default style is 'painterly' — the flat grid
  // with terrain art — so this is false unless the player opted into iso, and
  // even then only for a world whose terrains all have art. A world with none
  // (interiors: floor/wall/door/feature; legacy 'unknown') renders flat
  // wholesale rather than as an all-vector iso scene nobody has art-directed.
  // This is a mount-time choice: hexes streamed in later by addHexes never
  // re-run it, which is why IsoRenderer carries its own per-tile vector
  // fallback for a terrain that arrives without art.
  const useIso = mapStyle() === 'iso' && terrains.size > 0 && terrainTilesLoaded(terrains);

  const camera = new Container();
  app.stage.addChild(camera);
  // tileLayer (bottom) and tokenLayer (top) are owned by the Renderer,
  // constructed below — it inserts them around these three so the final
  // z-order matches today's: tiles, roads, overlays, locations, tokens.
  const roadLayer = new Container();
  const overlayLayer = new Container();
  const locationLayer = new Container();
  camera.addChild(roadLayer);
  camera.addChild(overlayLayer);
  camera.addChild(locationLayer);

  const hexData = new Map<string, HexInput>(
    opts.hexes.map((h) => [`${h.q},${h.r}`, h]),
  );
  const discoveredKeys = new Set(opts.discovered.map((d) => `${d.q},${d.r}`));
  // Mirrors TokenHandle.isSelf from the old token-registry so moveCharacter
  // can still tell whether the moved token is the player's own without the
  // Renderer interface needing to report it back.
  const selfCharacterIds = new Set<number>(opts.characters.filter((c) => c.isSelf).map((c) => c.id));

  // Structural pass: roads repaint whenever the known set (addHexes) or the
  // discovered set (discover) changes — both are cheap full repaints of one
  // Graphics (a world carries a few hundred segments at most).
  const roads = opts.roads ?? [];
  const roadGraphics = new Graphics();
  roadLayer.addChild(roadGraphics);
  const toPixel = (q: number, r: number): { x: number; y: number } => {
    if (useIso) {
      const { sx, groundY } = isoProject(q, r);
      return { x: sx, y: groundY };
    }
    const h = new HexTile({ q, r });
    return { x: h.x, y: h.y };
  };
  const refreshRoads = (): void => {
    drawRoads(roadGraphics, roadSegments(roads, new Set(hexData.keys())), discoveredKeys, toPixel);
  };

  let currentQ = opts.startQ;
  let currentR = opts.startR;
  let clickHandler: ((q: number, r: number) => void) | null = null;
  let rightClickHandler: ((q: number, r: number) => void) | null = null;
  let hoverKey: string | null = null;
  let needHexesHandler: ((q: number, r: number, radius: number) => void) | null = null;

  // Phase 2.5 fog of war: per-tile state is visible / foggy / unknown.
  // Recomputed on every move via refreshFog().
  let viewKeys = computeViewKeys(currentQ, currentR);

  const rendererCtx: RendererCtx = {
    app,
    camera,
    hexData,
    discoveredKeys,
    visibilityFor: (key, discovered) => visibilityFor(viewKeys, key, discovered),
    onTileHover: (_q, _r, key) => { hoverKey = key; refreshOverlays(); },
    onTileHoverOut: (key) => { if (hoverKey === key) { hoverKey = null; refreshOverlays(); } },
    onTileClick: (q, r) => {
      if (!clickHandler) return;
      if (!isAdjacent(q, r, currentQ, currentR)) return;
      clickHandler(q, r);
    },
    onTileRightClick: (q, r) => {
      if (!rightClickHandler) return;
      if (!discoveredKeys.has(`${q},${r}`)) return; // fog gate: discovered only
      rightClickHandler(q, r);
    },
  };
  // FlatRenderer wraps today's culling/token-registry code unchanged;
  // IsoRenderer (Tasks 3-5) implements the same Renderer interface for the
  // isometric projection, selected by the gate above. `flatRenderer` keeps the
  // concrete type in scope (null under iso) for the one FlatRenderer-only
  // escape hatch (getMountedTileGraphics, used by the fog/discover
  // reveal-fade below) that doesn't belong in the cross-renderer interface —
  // IsoRenderer has no equivalent tween, fog there is a sprite tint only.
  const flatRenderer = useIso ? null : new FlatRenderer(rendererCtx);
  const renderer: Renderer = useIso ? new IsoRenderer(rendererCtx, getTerrainTexture) : (flatRenderer as FlatRenderer);

  // Camera changes and data changes both schedule a relayout on the next
  // animation frame so a long drag fires at most once per frame even if
  // the user generates many pointermoves.
  let pendingRelayout = false;
  const invalidateLayout = (): void => {
    if (pendingRelayout) return;
    pendingRelayout = true;
    requestAnimationFrame(() => { pendingRelayout = false; renderer.relayout(); });
  };

  // Under iso, rings need the rotated/rescaled outline (isoHexCorners), not
  // the flat pointy-top HexTile corners — otherwise they'd be both hidden
  // (see the IsoRenderer z-order fix above) AND mis-shaped/mis-placed even
  // once visible.
  const cornersFor = (q: number, r: number): Array<{ x: number; y: number }> =>
    useIso ? isoHexCorners(q, r) : new HexTile({ q, r }).corners;

  const overlayCtx: OverlayCtx = {
    adjacencyRing: new Graphics(),
    currentRing: new Graphics(),
    hoverRing: new Graphics(),
    currentQ, currentR,
    hoverKey,
    cornersFor,
  };
  overlayLayer.addChild(overlayCtx.adjacencyRing);
  overlayLayer.addChild(overlayCtx.currentRing);
  overlayLayer.addChild(overlayCtx.hoverRing);

  const refreshOverlays = (): void => {
    overlayCtx.currentQ = currentQ;
    overlayCtx.currentR = currentR;
    overlayCtx.hoverKey = hoverKey;
    renderOverlays(overlayCtx);
  };

  // Centre the camera on the spawn so the player sees themselves at the
  // viewport centre. Flat tiles are pointy-top HexTile pixels; under iso,
  // tiles live at isoProject(q,r) instead — centering on the flat position
  // there would put a non-origin spawn off-screen and relayout would cull
  // everything (blank scene).
  if (useIso) {
    const { sx, groundY } = isoProject(opts.startQ, opts.startR);
    camera.x = app.renderer.width / 2 - sx;
    camera.y = app.renderer.height / 2 - groundY;
  } else {
    const startHex = new HexTile({ q: opts.startQ, r: opts.startR });
    camera.x = app.renderer.width / 2 - startHex.x;
    camera.y = app.renderer.height / 2 - startHex.y;
  }

  for (const c of opts.characters) renderer.addToken(c, 'character', c.isSelf);
  for (const n of opts.npcs ?? []) renderer.addToken(n, 'npc', false);

  for (const loc of opts.locations ?? []) {
    addDoorwayMarker(
      loc,
      locationLayer,
      useIso ? (q, r) => { const p = isoProject(q, r); return { x: p.sx, y: p.groundY }; } : undefined,
      useIso ? FACE_W * 0.32 : undefined,
    );
  }

  installCameraControls(app.canvas as HTMLCanvasElement, camera, { onChange: invalidateLayout });

  refreshRoads();
  refreshOverlays();
  renderer.relayout(); // synchronous first paint so opening the scene has tiles.

  // After a self-move the view ring shifts; every old visible-tile that's
  // no longer in view fogs over, every new visible-tile lights up. Cheap
  // — at most 12 tiles change per move.
  const refreshFog = (): void => {
    const prev = viewKeys;
    viewKeys = computeViewKeys(currentQ, currentR);
    for (const key of touchedFogKeys(prev, viewKeys)) {
      renderer.redrawTile(key);
      // Brighten tiles entering the view ring in; tiles leaving just redraw
      // to the dimmer fog colour without a fade (no flash on departure).
      if (viewKeys.has(key) && !prev.has(key)) {
        const g = flatRenderer?.getMountedTileGraphics(key);
        if (g) { g.alpha = 0.55; tweenAlpha(app.ticker, g, 1, 220); }
      }
    }
  };

  // Phase B3 streaming trigger: whenever the player walks far enough from
  // the centre of the last loaded disc we ask the runtime to fetch a new
  // one around their current position. The runtime calls addHexes/addNpcs
  // when the response lands.
  const checkStreamTrigger = createStreamTrigger(
    { q: opts.startQ, r: opts.startR },
    () => needHexesHandler,
  );

  return {
    setTerrain(q, r, terrain) {
      const key = `${q},${r}`;
      const data = hexData.get(key);
      if (!data) return;
      data.terrain = terrain;
      renderer.redrawTile(key);
    },
    discover(q, r) {
      const key = `${q},${r}`;
      discoveredKeys.add(key);
      refreshRoads();
      renderer.redrawTile(key);
      // Fade the freshly-revealed tile in from near-dark — the canonical
      // fog-of-war reveal. Only mounted (on-screen) tiles animate.
      const g = flatRenderer?.getMountedTileGraphics(key);
      if (g) { g.alpha = 0.4; tweenAlpha(app.ticker, g, 1, 260); }
    },
    addCharacter(c) {
      renderer.addToken(c, 'character', c.isSelf);
      if (c.isSelf) selfCharacterIds.add(c.id);
    },
    removeCharacter(characterId) {
      renderer.removeToken(characterId, 'character');
      selfCharacterIds.delete(characterId);
    },
    moveCharacter(characterId, q, r) {
      renderer.moveToken(characterId, 'character', q, r);
      if (selfCharacterIds.has(characterId)) {
        currentQ = q; currentR = r;
        refreshOverlays();
        refreshFog();
        checkStreamTrigger(q, r);
      }
    },
    setSelfPosition(q, r) {
      currentQ = q; currentR = r;
      refreshOverlays();
      refreshFog();
      checkStreamTrigger(q, r);
    },
    addNpc(n) { renderer.addToken(n, 'npc', false); },
    removeNpc(npcId) { renderer.removeToken(npcId, 'npc'); },
    moveNpc(npcId, q, r) { renderer.moveToken(npcId, 'npc', q, r); },
    onHexClick(handler) { clickHandler = handler; },
    onHexRightClick(handler) { rightClickHandler = handler; },
    destroy() {
      renderer.destroy();
      // Pixi v8: { children: true } tears down every container; removeView
      // drops the canvas. Runtime calls this on character.world.changed.
      app.destroy({ removeView: true }, { children: true });
    },
    addHexes(hexes) {
      // Phase B3: streamed hexes get folded into hexData. Tiles already
      // mounted are redrawn (terrain may have changed); fresh tiles wait
      // for the next relayout to be mounted only if inside the viewport.
      for (const h of hexes) {
        const key = `${h.q},${h.r}`;
        hexData.set(key, h);
      }
      refreshRoads();
      invalidateLayout();

      // The mount only fetched the bootstrap set's terrains. Travel can reveal
      // a terrain we've never drawn — fetch its art and repaint, otherwise it
      // would stay on the vector fallback for the rest of the session.
      const missing = [...new Set(hexes.map((h) => h.terrain))]
        .filter((t) => TERRAIN_TEXTURE[t] && !getTerrainTexture(t));
      if (missing.length > 0) {
        void loadHexAtlas(missing).then(() => { invalidateLayout(); });
      }
    },
    onNeedHexes(handler) { needHexesHandler = handler; },
  };
}
